
import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  AssetDetailsResponse,
  AssetInfoItemsResponse,
  AssetListItem,
  AssetListRequest,
  AssetServiceContractResponse,
  AssetsFuzzySearchProps,
  AggregatedStatusEnum,
  ServiceContractTypes,
  AssetsWildcardSearchProps,
  CalculatedContractStatusEnum,
  CustomerWidgetAssetListResponse,
  FirmwareAvailabilityStatusEnum
} from '@uptime-bff/api-contract';
import type { RootState } from 'core/store';
import { countSecondaryStatusTags, lengthOf } from './utils';

export type ListFiltersType = AssetListRequest['filters'];
type ListSortingType = AssetListRequest['sorting'];
export enum SearchType {
  Fuzzy = 'FUZZY',
  Wildcard = 'WILDCARD',
}
type ListFilterSearchType = {
  query: string;
  searchType: SearchType;
  field: AssetsFuzzySearchProps | AssetsWildcardSearchProps;
};

export const defaultState: AssetListState = {
  activeServiceContracts: [],
  activeListFilters: undefined,
  assetList: {},
  assetListSorting: undefined,
  assetMapActiveAssetId: undefined,
  assetSearch: {
    query: '',
    field: AssetsFuzzySearchProps.AssetName,
    searchType: SearchType.Fuzzy,
  },
  selectedAsset: undefined,
  firmwareAvailabilityStatus: undefined,
  servicesExpiryStatus: undefined,
};

interface AssetListState {
  activeServiceContracts: AssetServiceContractResponse;
  assetSearch?: ListFilterSearchType;
  activeListFilters: ListFiltersType;
  assetList: { [key: string]: AssetListItem };
  assetListSorting?: ListSortingType;
  assetMapActiveAssetId?: string;
  selectedAsset?: AssetListItem | AssetDetailsResponse | CustomerWidgetAssetListResponse;
  firmwareAvailabilityStatus?: FirmwareAvailabilityStatusEnum[];
  servicesExpiryStatus?: CalculatedContractStatusEnum[];
}

const assetListToMap = (list: AssetListItem[]): { [key: string]: AssetListItem } => {
  const payloadAssetsMap: { [key: string]: AssetListItem } = {};
  for (const asset of list) {
    payloadAssetsMap[asset.id] = asset;
  }
  return payloadAssetsMap;
};

const slice = createSlice({
  name: 'assets',
  initialState: defaultState,
  reducers: {
    setActiveServiceContracts: (state, action: PayloadAction<AssetServiceContractResponse | undefined>) => {
      state.activeServiceContracts = action.payload ?? [];
    },

    setSelectedAsset: (
      state,
      action: PayloadAction<(AssetListItem | AssetDetailsResponse | CustomerWidgetAssetListResponse) | undefined>
    ) => {
      state.selectedAsset = action.payload;
    },

    setSortingOnActiveSearch: (state, action: PayloadAction<ListSortingType>) => {
      state.assetListSorting = action.payload;
    },

    setFilterByIdOnActiveSearch: (state, action: PayloadAction<string>) => {
      state.activeListFilters = { ...state.activeListFilters, id: action.payload };
    },
    setFilterByModelsOnActiveSearch: (state, action: PayloadAction<string[]>) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        models: action.payload.length > 0 ? action.payload : undefined,
      };
    },

    setSearchOrFilterQuery: (state, action: PayloadAction<string>) => {
      state.assetSearch = {
        field: state.assetSearch?.field ?? AssetsFuzzySearchProps.AssetName,
        query: action.payload,
        searchType: state.assetSearch?.searchType ?? SearchType.Fuzzy,
      };
      slice.caseReducers.setSearchOnActiveSearch(state);
    },

    setSearchOnActiveSearch: (state) => {
      const checkLength = state.assetSearch?.query.length
        && state.assetSearch?.query.length > 1
        && state.assetSearch?.field?.length && state.assetSearch?.field.length > 0;

      if (checkLength && state.assetSearch?.searchType === SearchType.Fuzzy) {
        state.activeListFilters = {
          ...state.activeListFilters,
          search: {
            fields: [state.assetSearch.field as AssetsFuzzySearchProps],
            query: state.assetSearch.query,
          },
          wildcardSearch: undefined,
        };
      } else if (checkLength && state.assetSearch?.searchType === SearchType.Wildcard) {
        state.activeListFilters = {
          ...state.activeListFilters,
          search: undefined,
          wildcardSearch: {
            field: state.assetSearch.field as AssetsWildcardSearchProps,
            query: state.assetSearch.query,
          },
        };
      } else {
        state.activeListFilters = {
          ...state.activeListFilters,
          search: undefined,
          wildcardSearch: undefined,
        };
      }
    },

    clearFuzzySearchOnActiveSearch: (state) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        search: undefined,
        wildcardSearch: undefined,
        id: undefined,
        diamGroupId: undefined,
        companies: undefined,
      };
      state.assetSearch = {
        field: AssetsFuzzySearchProps.AssetName,
        query: '',
        searchType: SearchType.Fuzzy,
      };
    },

    setFilterByCompaniesByActiveCompanies: (
      state,
      action: PayloadAction<{ diamGroupIds: string; companies: string }>
    ) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        companies: [action.payload.companies],
        diamGroupId: [action.payload.diamGroupIds],
      };
      state.assetSearch = {
        ...state.assetSearch,
        query: action.payload.companies,
        searchType: SearchType.Fuzzy,
        field: AssetsFuzzySearchProps.Company,
      };
    },

    setFilterByCompaniesOnActiveSearch: (state, action: PayloadAction<{ diamGroupIds: string[] }>) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        diamGroupId: action.payload.diamGroupIds.length > 0 ? action.payload.diamGroupIds : undefined,
      };
    },
    setFilterByCompanyCountryOnActiveSearch: (state, action: PayloadAction<string[]>) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        companyCountries: action.payload.length > 0 ? action.payload : undefined,
      };
    },
    setFilterByCountriesOnActiveSearch: (state, action: PayloadAction<string[]>) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        companyCountries: action.payload,
      };
    },

    setFilterByStatusOnActiveSearch: (state, action: PayloadAction<string[]>) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        secondaryStatus: action.payload.length > 0 ? action.payload : undefined,
      };
    },

    setFilterByOperationalStatusOnActiveSearch: (state, action: PayloadAction<string[]>) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        operationalStatus: action.payload.length > 0 ? action.payload : undefined,
      };
    },
    setFilterByFirmwareAvailabilityStatusOnActiveSearch: (state, action: PayloadAction<FirmwareAvailabilityStatusEnum[]>) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        firmwareAvailabilityStatus: action.payload.length > 0 ? action.payload : undefined,
      };
    },

    setFilterByCalculatedContractStatusOnActiveSearch: (state, action: PayloadAction<CalculatedContractStatusEnum[]>) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        servicesExpiryStatus: action.payload.length > 0 ? action.payload : undefined,
      };
    },

    // ---- search abr
    // option 1 (fuzzy search)
    setFuzzySearchField: (
      state,
      action: PayloadAction<{ field: AssetsFuzzySearchProps; selected: boolean; value: string }>
    ) => {
      if (action.payload.selected) {
        state.assetSearch = {
          field: action.payload.field,
          searchType: SearchType.Fuzzy,
          query: state.assetSearch?.query ?? '',
        };
      }
      if (state?.assetSearch?.query && state.assetSearch.query.length > 1) {
        slice.caseReducers.setSearchOnActiveSearch(state);
      }
    },

    setWildcardSearchField: (
      state,
      action: PayloadAction<{ field: AssetsWildcardSearchProps; selected: boolean; value: string }>
    ) => {
      if (action.payload.selected) {
        state.assetSearch = {
          field: action.payload.field,
          searchType: SearchType.Wildcard,
          query: state.assetSearch?.query ?? '',
        };
      }
      if (state.assetSearch?.query && state.assetSearch.query.length > 1) {
        slice.caseReducers.setSearchOnActiveSearch(state);
      }
    },

    setFilterByServiceContractStatusOnActiveSearch: (
      state,
      action: PayloadAction<AggregatedStatusEnum | undefined>
    ) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        serviceContractStatus: action.payload ?? undefined,
      };
    },
    setFilterByHfsServiceContractStatusOnActiveSearch: (state, action: PayloadAction<ServiceContractTypes[]>) => {
      if (action.payload.includes(ServiceContractTypes.WithoutServices) && action.payload.length === 1) {
        state.activeListFilters = {
          ...state.activeListFilters,
          hfsServiceContractType: [ServiceContractTypes.WithoutServices],
        };
      } else {
        const filtered = action.payload.filter((type) => type !== ServiceContractTypes.WithoutServices);
        state.activeListFilters = {
          ...state.activeListFilters,
          hfsServiceContractType: filtered.length > 0 ? filtered : undefined,
        };
      }
    },

    appendAssetsToList: (state, action: PayloadAction<AssetListItem[]>) => {
      state.assetList = { ...state.assetList, ...assetListToMap(action.payload) };
    },

    updateAssetInfoItemInList: (
      state,
      action: PayloadAction<{ asset: AssetDetailsResponse; infoItem: AssetInfoItemsResponse }>
    ) => {
      state.assetList = {
        ...state.assetList,
        [action.payload.asset.iprId]: {
          ...state.assetList[action.payload.asset.iprId],
          status: {
            ...state.assetList[action.payload.asset.iprId]?.status,
            listInfoItems: {
              errorCode: action.payload.infoItem.infoItems?.[0]?.errorCode,
              localTimestampOn: action.payload.infoItem.infoItems?.[0]?.localTimestampOn,
              infoItemId: action.payload.infoItem.infoItems?.[0]?.infoItemId,
              reminderDate: action.payload.infoItem.infoItems?.[0]?.reminderDate,
            },
            secondaryStatus: action.payload.asset.status?.secondaryStatusTranslationKey,
            operationalStatus: action.payload.asset.status?.operationalStatus,
          },
        },
      };
    },

    setAssetMapActiveAssetId: (state, action: PayloadAction<string | undefined>) => {
      state.assetMapActiveAssetId = action.payload;
    },
    setAssetList: (state, action: PayloadAction<AssetListItem[]>) => {
      state.assetList = assetListToMap(action.payload);
    },
    clearFilters: (state) => {
      state.activeListFilters = undefined;
      state.assetListSorting = undefined;
      state.assetSearch = {
        field: AssetsFuzzySearchProps.AssetName,
        query: '',
        searchType: SearchType.Fuzzy,
      };
    },
    clearCompanyNameFilters: (state) => {
      state.activeListFilters = {
        ...state.activeListFilters,
        diamGroupId: undefined,
      };
    },
  },
});

export const {
  setActiveServiceContracts,
  setSelectedAsset,
  setFilterByCompaniesByActiveCompanies,
  setFilterByCompaniesOnActiveSearch,
  setFilterByCountriesOnActiveSearch,
  setFilterByIdOnActiveSearch,
  setFilterByStatusOnActiveSearch,
  setFilterByModelsOnActiveSearch,
  setFilterByOperationalStatusOnActiveSearch,
  setFilterByServiceContractStatusOnActiveSearch,
  setFilterByHfsServiceContractStatusOnActiveSearch,
  appendAssetsToList,
  setAssetList,
  updateAssetInfoItemInList,
  setAssetMapActiveAssetId,
  clearFilters,
  clearCompanyNameFilters,
  setFilterByCompanyCountryOnActiveSearch,
  clearFuzzySearchOnActiveSearch,
  setSortingOnActiveSearch,
  setFuzzySearchField,
  setSearchOrFilterQuery,
  setWildcardSearchField,
  setSearchOnActiveSearch,
  setFilterByFirmwareAvailabilityStatusOnActiveSearch,
  setFilterByCalculatedContractStatusOnActiveSearch
} = slice.actions;
export default slice.reducer;

export const selectActiveFiltersCount = createSelector(
  (state: RootState) => state.assets.activeListFilters,
  (filters) =>
    lengthOf(filters?.operationalStatus)
    + countSecondaryStatusTags(filters?.secondaryStatus)
    + lengthOf(filters?.firmwareAvailabilityStatus)
    + lengthOf(filters?.servicesExpiryStatus)
    + lengthOf(filters?.hfsServiceContractType)
    + lengthOf(filters?.models)
    + lengthOf(filters?.diamGroupId)
    + lengthOf(filters?.companyCountries)
);
